import { Component } from '@theme/component';

/** Space kept between a message and the desktop links (px). */
const LINKS_GAP = 24;

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {ResizeObserver|undefined} */
  #fitObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();

    const bar = this.closest('.announcement-bar');
    const links = bar?.querySelector('.announcement-bar__links');
    if (bar && links) {
      this.#fitObserver = new ResizeObserver(() => this.#updateFit(links));
      this.#fitObserver.observe(bar);
      this.#fitObserver.observe(links);
      // The web font loading widens the text without a resize.
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.#updateFit(links);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    this.#fitObserver?.disconnect();
    this.#fitObserver = undefined;
  }

  /**
   * EA-4425: the desktop links sit over the bar's right side. When a message
   * would run under them (or has to wrap while centered), [data-align-start]
   * moves the messages to the start, clear of the links. Measured centered and
   * set again in the same frame, so it never flickers.
   * @param {HTMLElement} links
   */
  #updateFit(links) {
    this.removeAttribute('data-align-start');
    if (!links.offsetWidth) return; // links hidden (below desktop)

    const linksLeft = links.getBoundingClientRect().left;
    this.style.setProperty(
      '--announcement-links-space',
      `${Math.ceil(this.getBoundingClientRect().right - linksLeft + LINKS_GAP)}px`,
    );

    const crowded = [...this.querySelectorAll('.announcement-bar__text')].some((text) => {
      const range = document.createRange();
      range.selectNodeContents(text);
      const lines = new Set([...range.getClientRects()].map((rect) => Math.round(rect.top)));
      return range.getBoundingClientRect().right > linksLeft - LINKS_GAP || lines.size > 1;
    });
    if (crowded) this.setAttribute('data-align-start', '');
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
